import * as d3 from 'd3';

const shouldDrawOnRightSide = (startAngle, endAngle) =>
  startAngle + (endAngle - startAngle) / 2 < Math.PI;

class DoughnutChartSvg {
  constructor({
    containerRef,
    small,
    width,
    height,
    margin,
    data,
    colors,
    labels,
    fontSize = 16,
    overlapSize = 20,
  }) {
    this.containerRef = containerRef;
    this.small = small;
    this.width = width;
    this.height = height;
    this.colors = colors;
    this.margin = margin;
    this.radius = Math.min(width, height) / 2 - this.margin;
    this.labels = labels;
    this.data = data;
    this.fontSize = fontSize;
    this.overlapSize = overlapSize;
    this.initialized = false;
  }

  init() {
    this.svg = d3.select(this.containerRef).append('svg');

    this.container = this.svg
      .attr('width', this.width)
      .attr('height', this.height)
      .style('transform', `${this.small ? 'scale(0.5)' : 'none'}`)
      .style('margin', `${this.small ? '-80px 0' : '0'}`)
      .style('overflow', `${this.small ? 'inherit' : 'hidden'}`)
      .append('g')
      .attr('transform', `translate(${this.width / 2},${this.height / 2})`);

    this.drawChart();
    this.initialized = true;
  }

  translateContainer({ x = 0, y = 0 }) {
    this.container.attr('transform', `translate(${this.width / 2 + x},${this.height / 2 + y})`);
  }

  getPolylinesPoints = ({ items, rightSide, arc, outerArc }) => {
    // reverse right side items for drawing
    const resultPolylines = (rightSide ? items.reverse() : items).reduce(
      (polylines, dataItem, index) => {
        const prevPolyline = index > 0 ? polylines[index - 1] : undefined;
        const prevBendPos = prevPolyline ? prevPolyline[1] : undefined;

        const bendPos = outerArc.centroid(dataItem);

        if (
          prevBendPos &&
          (bendPos[1] - prevBendPos[1] > 0 ||
            Math.abs(prevBendPos[1] - bendPos[1]) < this.overlapSize)
        ) {
          bendPos[1] = prevBendPos[1] - this.overlapSize;
        }

        const posA = arc.centroid(dataItem); // line insertion in the slice
        const posB = bendPos; // line break: we use the other arc generator that has been built only for that
        const posC = [...bendPos]; // Label position = almost the same as posB

        posC[0] = this.radius * 0.95 * (rightSide ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left

        return [...polylines, [posA, posB, posC]];
      },
      [],
    );
    return rightSide ? resultPolylines.reverse() : resultPolylines; // reverse right side polylines back after drawing
  };

  drawChart() {
    // Compute the position of each group on the pie:
    const pie = d3
      .pie()
      .sort(null) // Do not sort group by size
      .value(d => d.value);
    const dataReady = pie(d3.entries(this.data));

    // The arc generator
    const arc = d3
      .arc()
      .innerRadius(this.radius * 0.5) // This is the size of the donut hole
      .outerRadius(this.radius * 0.7);

    // Another arc that won't be drawn. Just for labels positioning
    const outerArc = d3
      .arc()
      .innerRadius(this.radius * 0.8)
      .outerRadius(this.radius * 0.8);

    // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
    this.container
      .selectAll('allSlices')
      .data(dataReady)
      .enter()
      .append('path')
      .attr('d', arc)
      .attr('fill', d => this.colors[d.index])
      .attr('stroke', 'white')
      .style('stroke-width', '2px')
      .style('opacity', 1.0);

    this.drawLabels({ data: dataReady, arc, outerArc });
  }

  checkOverflow(labelTextsPos) {
    let maxOverflow = { value: 0, positive: false };
    labelTextsPos.forEach(([, y]) => {
      const overflow = Math.max(maxOverflow.value, Math.abs(y) + this.fontSize - this.height / 2);
      if (overflow !== maxOverflow.value) {
        maxOverflow = { value: overflow, positive: y > 0 };
      }
    });

    const { value, positive } = maxOverflow;
    if (value > 0) {
      this.translateContainer({ y: positive ? -value : value });
    }
  }

  drawLabels({ data, arc, outerArc }) {
    const leftItems = data.filter(
      ({ startAngle, endAngle }) => !shouldDrawOnRightSide(startAngle, endAngle),
    );

    const rightItems = data.filter(({ startAngle, endAngle }) =>
      shouldDrawOnRightSide(startAngle, endAngle),
    );

    const polylinesPoints = [
      ...this.getPolylinesPoints({ items: rightItems, rightSide: true, arc, outerArc }),
      ...this.getPolylinesPoints({ items: leftItems, rightSide: false, arc, outerArc }),
    ];

    // Add the polylines between chart and labels:
    this.container
      .selectAll('allPolylines')
      .data(data)
      .enter()
      .append('polyline')
      .attr('stroke', 'black')
      .style('fill', 'none')
      .attr('stroke-width', 1)
      .attr('points', (_d, index) => {
        return polylinesPoints[index];
      });

    // Calculate positions for label texts
    const labelTextsPos = data.map(({ startAngle, endAngle }, index) => [
      this.radius * 0.99 * (shouldDrawOnRightSide(startAngle, endAngle) ? 1 : -1),
      polylinesPoints[index][1][1],
    ]);

    // Check if any of the texts is overflowing and if so, translate the container
    this.checkOverflow(labelTextsPos);

    // Draw label texts:
    this.container
      .selectAll('allLabels')
      .data(data)
      .enter()
      .append('text')
      .text(d => `${d.data.value}%`)
      .attr('transform', (d, index) => `translate(${labelTextsPos[index]})`)
      .style('text-anchor', d => {
        return shouldDrawOnRightSide(d.startAngle, d.endAngle) ? 'start' : 'end';
      })
      .style('font-size', `${this.fontSize}px`);
  }

  destroy() {
    if (this.initialized) {
      this.svg.remove();
      this.initialized = false;
    }
  }
}

export default DoughnutChartSvg;
